import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import ProtectedRoute from "@/components/ProtectedRoute";
import PolicyRoute from "@/components/PolicyRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import HomeGate from "@/components/HomeGate";
import Inventory from "@/pages/Inventory";
import { Customers, Suppliers } from "@/pages/SimplePages";
import POS from "@/pages/POS";
import Sales from "@/pages/Sales";
import { Quotations, DeliveryNotes } from "@/pages/Documents";
import CreditDebitNotes from "@/pages/CreditDebitNotes";
import Purchases from "@/pages/Purchases";
import Accounting from "@/pages/Accounting";
import CxcMigration from "@/pages/CxcMigration";
import ReceivablesAging, { ReceivablesAgingDetail } from "@/pages/ReceivablesAging";
import Receipts from "@/pages/Receipts";
import CustomerStatement from "@/pages/CustomerStatement";
import Reports from "@/pages/Reports";
import Payroll from "@/pages/Payroll";
import CashSessions from "@/pages/CashSessions";
import ModuleRoute from "@/components/ModuleRoute";
import Settings from "@/pages/Settings";
import Help from "@/pages/Help";
import SalesOrdersQueue from "@/pages/SalesOrdersQueue";
import RolesSettings from "@/pages/RolesSettings";
import OrdersAuditReport from "@/pages/OrdersAuditReport";
import EcfRecibidos from "@/pages/EcfRecibidos";
import Expenses from "@/pages/Expenses";
import Fiscal from "@/pages/Fiscal";

function App() {
  return (
    <div className="App">
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <BrowserRouter>
          <AuthProvider>
            <BrandProvider>
            <ConfirmProvider>
              <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<HomeGate><Dashboard /></HomeGate>} />
              <Route path="/pos" element={<PolicyRoute policy="pos.create_cart"><POS /></PolicyRoute>} />
              {/* Inventario consolidado (Productos / Categorías / Almacenes) */}
              <Route path="/inventory" element={<PolicyRoute policy="inventory.view"><Inventory /></PolicyRoute>} />
              <Route path="/inventory/categories" element={<PolicyRoute policy="inventory.view"><Inventory /></PolicyRoute>} />
              <Route path="/inventory/subcategories" element={<PolicyRoute policy="inventory.view"><Inventory /></PolicyRoute>} />
              <Route path="/inventory/warehouses" element={<PolicyRoute policy="inventory.view"><Inventory /></PolicyRoute>} />
              {/* Legacy redirects (mantener para bookmarks / data-testids antiguos) */}
              <Route path="/products" element={<Navigate to="/inventory" replace />} />
              <Route path="/categories" element={<Navigate to="/inventory/categories" replace />} />
              <Route path="/subcategories" element={<Navigate to="/inventory/subcategories" replace />} />
              <Route path="/warehouses" element={<Navigate to="/inventory/warehouses" replace />} />

              <Route path="/sales" element={<PolicyRoute policy="sales.view"><Sales /></PolicyRoute>} />
              <Route path="/quotations" element={<PolicyRoute policy="sales.view"><Quotations /></PolicyRoute>} />
              <Route path="/delivery-notes" element={<PolicyRoute policy="sales.view"><DeliveryNotes /></PolicyRoute>} />
              <Route path="/credit-debit-notes" element={<PolicyRoute policy="sales.view"><CreditDebitNotes /></PolicyRoute>} />
              <Route path="/customers" element={<PolicyRoute policy="contacts.view"><Customers /></PolicyRoute>} />
              <Route path="/suppliers" element={<PolicyRoute policy="contacts.view"><Suppliers /></PolicyRoute>} />
              <Route path="/purchases" element={<PolicyRoute policy="purchases.view"><Purchases /></PolicyRoute>} />
              <Route path="/expenses" element={<PolicyRoute policy="purchases.view"><Expenses /></PolicyRoute>} />
              <Route path="/fiscal" element={<PolicyRoute policy="reports.view"><Fiscal /></PolicyRoute>} />
              <Route path="/ecf-recibidos" element={<PolicyRoute policy="purchases.view"><EcfRecibidos /></PolicyRoute>} />
              <Route path="/accounting" element={<PolicyRoute policy="accounting.view"><Accounting /></PolicyRoute>} />
              <Route path="/cxc-migration" element={<PolicyRoute policy="accounting.view"><CxcMigration /></PolicyRoute>} />
              <Route path="/receivables-aging" element={<PolicyRoute policy="accounting.view"><ReceivablesAging /></PolicyRoute>} />
              <Route path="/receivables-aging/:customerId" element={<PolicyRoute policy="accounting.view"><ReceivablesAgingDetail /></PolicyRoute>} />
              <Route path="/receipts" element={<PolicyRoute policy="finance.view"><Receipts /></PolicyRoute>} />
              <Route path="/customer-statements" element={<PolicyRoute policy="finance.view"><CustomerStatement /></PolicyRoute>} />
              <Route path="/reports" element={<PolicyRoute policy="reports.view"><Reports /></PolicyRoute>} />
              <Route path="/payroll" element={<PolicyRoute policy="accounting.view"><ModuleRoute module="hr_payroll"><Payroll /></ModuleRoute></PolicyRoute>} />
              <Route path="/cash-sessions" element={<PolicyRoute policy="pos.create_cart"><ModuleRoute module="cash_sessions"><CashSessions /></ModuleRoute></PolicyRoute>} />
              {/* /suite legacy redirect — contenido reagrupado en Contabilidad, Nómina y Caja Z/X */}
              <Route path="/suite" element={<Navigate to="/" replace />} />
              <Route path="/reports/orders-audit" element={<PolicyRoute policy="reports.audit_orders"><OrdersAuditReport /></PolicyRoute>} />
              <Route path="/sales-orders" element={<PolicyRoute policy="orders.view_queue"><SalesOrdersQueue /></PolicyRoute>} />
              {/* Migración movida a Ajustes → Datos; mantenemos redirect */}
              <Route path="/migration" element={<Navigate to="/settings?tab=data" replace />} />
              <Route path="/settings" element={<PolicyRoute policy="settings.view"><Settings /></PolicyRoute>} />
              <Route path="/settings/roles" element={<PolicyRoute policy="settings.manage_roles"><RolesSettings /></PolicyRoute>} />
              <Route path="/help" element={<Help />} />
            </Route>
          </Routes>
          </ConfirmProvider>
            </BrandProvider>
        </AuthProvider>
      </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
